import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { authService } from "../services/authService";
import { useAuthStore } from "../store/authStore";
import toast from "react-hot-toast";
import { Loader2, Package, UserCog, CheckCircle } from "lucide-react";

function ProductRoleSelector() {
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { setProductRole } = useAuthStore();

  useEffect(() => {
    fetchRolesAndProducts();
  }, []);

  const fetchRolesAndProducts = async () => {
    try {
      setLoading(true);
      const data = await authService.getRolesAndProducts();
      
      if (data.success && data.data?.products) {
        setProducts(data.data.products);
        
        // Auto-select if only one product and one role
        if (data.data.products.length === 1) {
          const product = data.data.products[0];
          setSelectedProduct(product);
          
          if (product.roles && product.roles.length === 1) {
            setSelectedRole(product.roles[0]);
          }
        }
      } else {
        toast.error("No products or roles available");
      }
    } catch (error) {
      console.error("Failed to fetch roles and products:", error);
      toast.error("Failed to load products and roles");
    } finally {
      setLoading(false);
    }
  };

  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    setSelectedRole(null); // Reset role when product changes
  };

  const handleRoleSelect = (role) => {
    setSelectedRole(role);
  };

  const handleConfirm = () => {
    if (!selectedProduct || !selectedRole) {
      toast.error("Please select both product and role");
      return;
    }

    // Store in localStorage and authStore
    setProductRole(selectedProduct.id, selectedRole.id, {
      productName: selectedProduct.product_name,
      roleName: selectedRole.name,
      userDivision: selectedRole.user_division,
    });

    toast.success(`Selected ${selectedProduct.product_name} - ${selectedRole.name}`);
    navigate("/home");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50 dark:from-gray-900 dark:via-gray-800 dark:to-stone-900 transition-colors duration-300 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-orange-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Loading products...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50 dark:from-gray-900 dark:via-gray-800 dark:to-stone-900 transition-colors duration-300 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-24 h-24 mx-auto mb-4 flex items-center justify-center">
            <img
              src="/pwa1-192x192.png"
              alt="Galaxy Logo"
              className="w-full h-full object-contain drop-shadow-2xl"
            />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Select Product & Role
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Choose your product and role to continue
          </p>
        </div>

        {/* Selection Cards */}
        <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl p-8 space-y-6 transition-colors duration-300">
          {/* Product Selection */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Package className="w-5 h-5 text-orange-500" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Select Product
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {products.map((product) => (
                <button
                  key={product.id}
                  onClick={() => handleProductSelect(product)}
                  className={`p-4 rounded-xl border-2 transition-all duration-200 text-left relative ${
                    selectedProduct?.id === product.id
                      ? "border-orange-500 bg-orange-50 dark:bg-orange-900/20"
                      : "border-gray-200 dark:border-gray-700 hover:border-orange-300 dark:hover:border-orange-700"
                  }`}
                >
                  <div className="font-semibold text-gray-900 dark:text-white">
                    {product.product_name}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {product.roles?.length || 0} role(s) available
                  </div>
                  {selectedProduct?.id === product.id && (
                    <CheckCircle className="w-5 h-5 text-orange-500 absolute top-4 right-4" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Role Selection */}
          {selectedProduct && selectedProduct.roles && selectedProduct.roles.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-4">
                <UserCog className="w-5 h-5 text-orange-500" />
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Select Role
                </h2>
              </div>
              <div className="grid grid-cols-1 gap-3">
                {selectedProduct.roles.map((role) => (
                  <button
                    key={role.id}
                    onClick={() => handleRoleSelect(role)}
                    className={`p-4 rounded-xl border-2 transition-all duration-200 text-left relative ${
                      selectedRole?.id === role.id
                        ? "border-orange-500 bg-orange-50 dark:bg-orange-900/20"
                        : "border-gray-200 dark:border-gray-700 hover:border-orange-300 dark:hover:border-orange-700"
                    }`}
                  >
                    <div className="font-semibold text-gray-900 dark:text-white">
                      {role.name}
                    </div>
                    {role.user_division && (
                      <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        Division: {role.user_division}
                      </div>
                    )}
                    {selectedRole?.id === role.id && (
                      <CheckCircle className="w-5 h-5 text-orange-500 absolute top-4 right-4" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Confirm Button */}
          <button
            onClick={handleConfirm}
            disabled={!selectedProduct || !selectedRole}
            className="w-full py-4 bg-gradient-to-r from-orange-500 to-amber-600 text-white font-semibold rounded-2xl shadow-lg hover:shadow-xl hover:from-orange-600 hover:to-amber-700 active:scale-[0.98] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100 flex items-center justify-center gap-2"
          >
            <CheckCircle className="w-5 h-5" />
            <span>Continue</span>
          </button>
        </div>

        {/* Info */}
        <p className="text-center text-sm text-gray-500 dark:text-gray-500 mt-6">
          You can change your selection anytime from the profile page
        </p>
      </div>
    </div>
  );
}

export default ProductRoleSelector;
